import React, { useState, useEffect, useMemo } from 'react';
import { Formik, Form, Field } from 'formik';
import Select from 'react-select';
import { Button as UdsButton, Spacer } from '@telus-uds/components-web';
import Modal from '../../design/shared/modal';
import StickyOrderModalHeader from '../../dashboards/dashboardCommon/StickyOrderModalHeader';
import ValidateLoader from '../../UI/validateLoader';

import { Button, ModalFooter, ErrorMessage } from './css';
import { getLocale } from '../../misc';
import EN from '../../../EN.json';
import FR from '../../../FR.json';
import { fibreCopperConsolidationField, initialFormValues } from './constant';
import {
  getDealerships,
  getFilterTableData,
  getTableData,
  getOfferCodesForDropdown,
  isAllOptionsSelected,
  RenderDynamicField,
  validationSchema
} from './utils';
import Filter from './filter';
import Table from './table';
import request from '../../utils/request';
import Pagination from '../../design/shared/table/pagination';
import ServiceDetailsTable from './serviceDetailTable';

const fibreConsolidation = ({ formFields, fieldName, fields, orderData }) => {
  const [isModalVisible, setModalVisible] = useState(false);
  const [isServicesVisible, setIsServicesVisible] = useState(false);
  const [dealers, setDealers] = useState([]);
  const [channels, setChannels] = useState([]);
  const [offerCodes, setOfferCodes] = useState([]);
  const [updateData, setUpdateData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingTable, setIsLoadingTable] = useState(true);
  const [initialValues, setInitialValues] = useState(null);
  const [offerPage, setOfferPage] = useState(1);
  const [filters, setFilters] = useState('');
  const [orderUpdate, setOrderUpdate] = useState(false);
  const [searchByOfferCode, setSearchByOfferCode] = useState('');
  const [validationError, setValidationError] = useState('');
  const [tableData, setTableData] = useState({
    orders: [],
    count: 0
  });
  const state = useMemo(() => ({ dealers, channels, offerCodes }), [dealers, channels, offerCodes]);
  const limit = 5;
  const locale = getLocale();
  const {
    vulcanWebform: { _vulOffer }
  } = locale === 'en-US' ? EN : FR;

  const editItem = (values, setFieldValue) => {
    setIsServicesVisible(true);
    setFieldValue('offerDescriptionEn', values.offerDescriptionEn);
    setFieldValue('offerDescriptionFr', values.offerDescriptionFr);
    setFieldValue('offerDetailsEn', values.offerDetailsEn);
    setFieldValue('offerDetailsFr', values.offerDetailsFr);
    setFieldValue('value', values.value);
    setFieldValue('frequency', values.frequency);
    setUpdateData(values);
  };

  const deleteItem = (e, idx, offerDetails, setFieldValue) => {
    if (e && e.preventDefault) e.preventDefault();
    setFieldValue(
      'offerDetails',
      offerDetails.filter((_, i) => i !== idx)
    );
  };

  const onCloseModal = () => {
    setModalVisible(false);
    setInitialValues(null);
    setIsServicesVisible(false);
  };

  const showModal = e => {
    e.preventDefault();
    setUpdateData(null);
    setModalVisible(true);
    setValidationError('');
  };

  const handleWheel = e => e.target.blur();

  useEffect(() => {
    getDealerships(setDealers, setChannels, setIsLoading);
  }, []);

  useEffect(() => {
    if (isModalVisible) {
      getOfferCodesForDropdown(setOfferCodes, setIsLoading);
    }
  }, [isModalVisible]);

  useEffect(() => {
    setIsLoadingTable(true);
    if (filters) {
      getFilterTableData({
        setTableData,
        setIsLoadingTable,
        limit,
        offerPage,
        filters
      });
    } else if (searchByOfferCode) {
      request
        .post(`/offers/filter?page=${offerPage || 1}&limit=${limit}`, {
          offerCode: searchByOfferCode
        })
        .then(response => {
          setTableData({
            orders: response.data.rows,
            count: response.data.count
          });
          setIsLoadingTable(false);
        });
    } else {
      getTableData({ setTableData, setIsLoadingTable, limit, offerPage });
    }
  }, [offerPage, filters, orderUpdate, searchByOfferCode]);

  return (
    <div>
      <Button>
        <UdsButton
          variant={{ priority: 'high' }}
          onClick={showModal}
          id="redeemOfferData"
          name="redeemOfferData"
        >
          {_vulOffer}
        </UdsButton>
      </Button>
      <hr />
      <Filter
        setFilters={setFilters}
        setOfferPage={setOfferPage}
        setSearchByOfferCode={setSearchByOfferCode}
      />
      <Table
        tableData={tableData}
        isLoading={isLoadingTable}
        setInitialValues={setInitialValues}
        setModalVisible={setModalVisible}
        setOrderUpdate={setOrderUpdate}
        setIsLoading={setIsLoadingTable}
      />
      <Pagination
        currentPage={parseInt(offerPage || 1, 10)}
        setSrtPage={setOfferPage}
        step={limit} // items per page
        count={tableData.count} // total number of items
      />
      {isModalVisible && <StickyOrderModalHeader />}
      <Modal
        open={isModalVisible}
        setOpen={onCloseModal}
        size="large"
        header={initialValues ? 'Update Offer' : _vulOffer}
      >
          {/* Marker that triggers the shared view-order purple header styling
              (floating rounded banner + shadow + white title/close) defined in
              assets/css/style.css, so this modal matches the view-order modals. */}
          <div className="view-order-details" />
          <Spacer space={4} />
          <Formik
            initialValues={initialValues ? { ...initialFormValues, ...initialValues } : initialFormValues}
            validationSchema={() => validationSchema(isServicesVisible, channels)}
            validateOnChange={false}
            validateOnBlur={false}
            onSubmit={async (values, { setFieldValue }) => {
              // Service-only update
              if (isServicesVisible) {
                const newService = {
                  id:
                    (updateData && updateData.id) != null
                      ? updateData.id
                      : values.offerDetails.length + 1,
                  offerDetailsEn: values.offerDetailsEn,
                  offerDetailsFr: values.offerDetailsFr,
                  value: values.value,
                  frequency: values.frequency
                };

                const updatedServices = updateData
                  ? values.offerDetails.map(s =>
                      s.id === updateData.id ? newService : s
                    )
                  : [...values.offerDetails, newService];

                // Optional: clear service-related fields after adding
                [
                  'offerDetailsEn',
                  'offerDetailsFr',
                  'value',
                  'frequency'
                ].forEach(field => setFieldValue(field, ''));

                setFieldValue('offerDetails', updatedServices);
                setIsServicesVisible(false);
                return;
              }
              try {
                setIsLoading(true);
                const url = initialValues ? `/offers/${values.id}` : '/offers';

                const method = initialValues ? 'put' : 'post';

                const response = await request[method](url, {
                  ...values,
                  allChannels: isAllOptionsSelected(values.channels, channels),
                  allDealerships: isAllOptionsSelected(values.dealerships, dealers)
                });
                if (response.status === 200) {
                  setModalVisible(false);
                  setOrderUpdate(prev => !prev);
                  setInitialValues(null);
                }
              }catch(err){
                if (err.response && err.response.data) {
                 setValidationError(err.response.data.message);
                }
              }finally {
                setIsLoading(false);
              }
            }}
          >
            {({ errors, values, setFieldValue, submitForm, validateForm, setTouched }) => {
              useEffect(() => {
                // if you want to store just the value array:
                if (dealers.length && values.dealerships.length === 0) {
                  setFieldValue(
                    'dealerships',
                    dealers.map(d => d.value) // store only values
                  );
                }
                if (values.bundled && !Array.isArray(values.bundled)) {
                  setFieldValue('bundled', [values.bundled]);
                }
                if (values.term && !Array.isArray(values.term)) {
                  setFieldValue('term', [values.term]);
                }
                if (values.offerCodesNotStackable && !Array.isArray(values.offerCodesNotStackable)) {
                  setFieldValue('offerCodesNotStackable', [values.offerCodesNotStackable]);
                }
                // Mandatory Validation Code is now Yes/No only; reset legacy free-text values.
                if (
                  values.validationCode != null &&
                  values.validationCode !== '' &&
                  values.validationCode !== 'Yes' &&
                  values.validationCode !== 'No'
                ) {
                  setFieldValue('validationCode', '');
                }
              }, [dealers]);

              return (
                <>
                  {isLoading && (
                    <ValidateLoader
                      message="Please wait request is In progress...."
                      isFormStep={isLoading}
                    />
                  )}
                  {!isLoading && (
                    <Form>
                      {validationError && <ErrorMessage>{validationError}</ErrorMessage>}
                      {fibreCopperConsolidationField.map(field => {

                        if (field.isVisible === isServicesVisible) {
                          return null;
                        }

                        // T019 [US4]: TSB Plan is only shown when at least one selected product is 'TSB'
                        if (field.name === 'tsbPlan' && !(values.product || []).includes('TSB')) {
                          return null;
                        }

                        const stateForField =
                          field.name === 'offerCodesNotStackable' && values.offerCode
                            ? {
                                ...state,
                                offerCodes: (state.offerCodes || []).filter(
                                  opt => opt.value !== values.offerCode
                                )
                              }
                            : state;

                        return (
                          <RenderDynamicField
                            field={field}
                            values={values}
                            errors={errors}
                            setFieldValue={setFieldValue}
                            state={stateForField}
                            handleWheel={handleWheel}
                            FormikField={Field}
                          />
                        );
                      })}
                      {!isServicesVisible && values.offerDetails.length > 0 && (
                          <ServiceDetailsTable
                            offerDetails={values.offerDetails}
                            editItem={editItem}
                            deleteItem={deleteItem}
                            setFieldValue={setFieldValue}
                            values={values}
                          />
                      )}
                      {
                        <ModalFooter>
                          {isServicesVisible && (
                            <Button>
                              <UdsButton
                                variant={{ priority: 'low' }}
                                onClick={() => setIsServicesVisible(false)}
                              >
                                Cancel
                              </UdsButton>
                            </Button>
                          )}
                          {!isServicesVisible && (
                            <Button>
                              <UdsButton
                                variant={{ priority: 'low' }}
                                onClick={() => setIsServicesVisible(true)}
                              >
                                Add Line Item(s)
                              </UdsButton>
                            </Button>
                          )}
                          <Button>
                            <UdsButton
                              variant={{ priority: 'high' }}
                              onClick={async () => {
                                const findErrors = await validateForm();
                                if (Object.keys(findErrors).length === 0) {
                                  submitForm();
                                } else {
                                  // Mark all invalid fields as touched so their error messages become visible
                                  const touchedFields = Object.keys(findErrors).reduce((acc, key) => {
                                    acc[key] = true;
                                    return acc;
                                  }, {});
                                  setTouched(touchedFields, false);
                                  console.log('[WirelineConsolidation] Validation errors blocking submit:', JSON.stringify(findErrors, null, 2));
                                }
                              }}
                              disabled={
                                values.offerDetails.length === 0 &&
                                !isServicesVisible
                              }
                            >
                              Add
                            </UdsButton>
                          </Button>
                        </ModalFooter>
                      }
                    </Form>
                  )}
                </>
              );
            }}
          </Formik>
      </Modal>
    </div>
  );
};

export default fibreConsolidation;
